import { useState, useEffect } from 'react';
import { FiDownload } from 'react-icons/fi';
import api from '../../utils/api';
import DataTable from '../../components/DataTable';
import Badge from '../../components/Badge';
import { formatDate, getCertStatus } from '../../utils/dateHelpers';
import { exportToExcel } from '../../utils/exportUtils';

export default function StaffOverview() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nearExpiry, setNearExpiry] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [s, ne] = await Promise.all([
          api.get('/staff'),
          api.get('/staff/near-expiry'),
        ]);
        setStaff(s.staff || []);
        setNearExpiry(ne.staff || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleExport = () => {
    const rows = staff.map((s) => ({
      'Employee ID': s.employee_id,
      'Name': s.staff_name,
      'Department': s.department,
      'Position': s.position,
      'Location': s.location,
      'BLS Status': s.bls_expiry_date ? getCertStatus(s.bls_expiry_date) : 'N/A',
      'BLS Expiry': s.bls_expiry_date || '',
      'ACLS Status': s.acls_expiry_date ? getCertStatus(s.acls_expiry_date) : 'N/A',
      'ACLS Expiry': s.acls_expiry_date || '',
      'ATLS Status': s.atls_expiry_date ? getCertStatus(s.atls_expiry_date) : 'N/A',
      'ATLS Expiry': s.atls_expiry_date || '',
      'NRP Status': s.nrp_expiry_date ? getCertStatus(s.nrp_expiry_date) : 'N/A',
      'NRP Expiry': s.nrp_expiry_date || '',
      'Grand Rounds': s.grand_round_count,
    }));
    exportToExcel(rows, 'staff_overview');
  };

  const handleExportNearExpiry = () => {
    const rows = nearExpiry.map((s) => ({
      'Employee ID': s.employee_id,
      'Name': s.staff_name,
      'Department': s.department,
      'Flagged Certs': (s.near_expiry_certs || []).join(', '),
      'BLS Expiry': s.bls_expiry_date,
      'ACLS Expiry': s.acls_expiry_date,
      'ATLS Expiry': s.atls_expiry_date,
      'NRP Expiry': s.nrp_expiry_date,
    }));
    exportToExcel(rows, 'near_expiry_by_department');
  };

  const columns = [
    { key: 'employee_id', label: 'ID' },
    { key: 'staff_name', label: 'Name' },
    { key: 'department', label: 'Department' },
    { key: 'position', label: 'Position' },
    { key: 'location', label: 'Location' },
    {
      key: 'bls_expiry_date', label: 'BLS',
      render: (val) => val ? <Badge status={getCertStatus(val)} text={`${getCertStatus(val)} - ${formatDate(val)}`} /> : <span style={{ color: '#B2AB9C' }}>N/A</span>,
    },
    {
      key: 'acls_expiry_date', label: 'ACLS',
      render: (val) => val ? <Badge status={getCertStatus(val)} text={`${getCertStatus(val)} - ${formatDate(val)}`} /> : <span style={{ color: '#B2AB9C' }}>N/A</span>,
    },
    {
      key: 'atls_expiry_date', label: 'ATLS',
      render: (val) => val ? <Badge status={getCertStatus(val)} text={`${getCertStatus(val)} - ${formatDate(val)}`} /> : <span style={{ color: '#B2AB9C' }}>N/A</span>,
    },
    {
      key: 'nrp_expiry_date', label: 'NRP',
      render: (val) => val ? <Badge status={getCertStatus(val)} text={`${getCertStatus(val)} - ${formatDate(val)}`} /> : <span style={{ color: '#B2AB9C' }}>N/A</span>,
    },
    { key: 'grand_round_count', label: 'Grand Rounds' },
  ];

  // Group near-expiry by department
  const grouped = {};
  nearExpiry.forEach((s) => {
    const dept = s.department || 'Unassigned';
    if (!grouped[dept]) grouped[dept] = [];
    grouped[dept].push(s);
  });

  return (
    <div>
      {/* Actions */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Staff Overview (Read-Only)</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-sm btn-secondary" onClick={handleExport}>
              <FiDownload size={14} /> Export All
            </button>
            <button className="btn btn-sm btn-secondary" onClick={handleExportNearExpiry}>
              <FiDownload size={14} /> Export Near Expiry
            </button>
          </div>
        </div>
      </div>

      {/* Full Staff Table */}
      {loading ? (
        <div className="card text-center p-4"><div className="spinner" style={{ margin: '40px auto' }} /></div>
      ) : (
        <DataTable columns={columns} data={staff} />
      )}

      {/* Near Expiry by Department */}
      {nearExpiry.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Near Expiry by Department</h3>
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([dept, members]) => (
            <div key={dept} className="card" style={{ marginBottom: '12px' }}>
              <h4 style={{ marginBottom: '8px' }}>{dept} <span className="text-sm text-muted">({members.length})</span></h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>ID</th>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Flagged Certs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((s) => (
                      <tr key={s.employee_id}>
                        <td style={tdStyle}>{s.employee_id}</td>
                        <td style={tdStyle}>{s.staff_name}</td>
                        <td style={tdStyle}>
                          {(s.near_expiry_certs || []).map((c) => <Badge key={c} status="expiring" text={c} />)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: '8px 12px', fontSize: '0.8rem', fontWeight: 600, textAlign: 'left', borderBottom: '2px solid #f0ece4', color: '#2B362E' };
const tdStyle = { padding: '8px 12px', fontSize: '0.85rem', borderBottom: '1px solid #f0ece4' };
